import { useMemo } from 'react'
import { Alert, Card, Progress, Skeleton, Space, Statistic, Typography, theme } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { EMPTY_TABLE_STATE } from '@/shared/ui/table/tableState'
import { fetchAuthLog } from '@/features/session/sessionApi'

/** Nechta oxirgi urinish tahlil qilinadi */
const SAMPLE = 100

/** Shu foizdan yuqori rad etish — ogohlantirish rangi */
const WARN_PERCENT = 20

/**
 * Ulanish sog'ligi — oxirgi urinishlarning nechtasi rad etilgan.
 *
 * <h2>Nega bu kerak</h2>
 * Rad etish darajasining KESKIN o'sishi bitta abonent muammosi emas,
 * TIZIM muammosi: NAS siri almashib ketgan, proyeksiya buzilgan yoki
 * paketlar tugagan. Bunday holatda qo'ng'iroqlar bir vaqtda keladi va
 * operator sababni izlashni bitta abonentdan boshlaydi — noto'g'ri
 * joydan.
 *
 * <h2>⚠️ Bu NAMUNA, mutlaq statistika emas</h2>
 * Oxirgi {@link SAMPLE} ta urinish olinadi, ya'ni oyna vaqtga emas,
 * SONGA bog'langan. Kam trafikli tunda u bir necha soatni, gavjum
 * paytda esa bir necha daqiqani qamrashi mumkin. Shuning uchun
 * "oxirgi N urinish" deb aniq yoziladi — "bugungi foiz" degan yolg'on
 * aniqlik berilmaydi.
 */
export function AuthHealthPanel() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { can } = usePermissions()
  const allowed = can('subscriber.read')

  const log = useQuery({
    queryKey: [...queryKeys.sessions.authLog, 'health', SAMPLE],
    queryFn: ({ signal }) =>
      fetchAuthLog({ ...EMPTY_TABLE_STATE, pageSize: SAMPLE }, signal),
    enabled: allowed,
    refetchInterval: 30_000,
  })

  const stats = useMemo(() => {
    const rows = log.data?.items ?? []
    const total = rows.length
    /*
      ⚠️ `accepted` MAYDONI ishlatiladi, xom `reply` satri EMAS.

      `sessionApi` uni allaqachon hisoblab qo'ygan va u yagona joyda
      turadi. Bu yerda satrni qayta tahlil qilish ikkinchi ta'rif
      bo'lardi va ular vaqt o'tib ajralib ketardi.
    */
    const rejected = rows.filter((r) => !r.accepted).length
    return { total, rejected, percent: total === 0 ? 0 : Math.round((rejected / total) * 100) }
  }, [log.data])

  if (!allowed) return null

  const bad = stats.percent >= WARN_PERCENT

  return (
    <Card
      title={t('dashboard.authHealth.title')}
      extra={<Link to="/sessions?tab=auth">{t('audit.openFull')}</Link>}
      size="small"
      style={{ height: '100%' }}
    >
      {log.isError ? (
        <Alert type="error" showIcon message={translateError(log.error, t)} />
      ) : log.isPending ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : stats.total === 0 ? (
        <Typography.Text type="secondary">{t('dashboard.authHealth.empty')}</Typography.Text>
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Statistic
            title={t('dashboard.authHealth.rejected')}
            value={stats.percent}
            suffix="%"
            valueStyle={{ color: bad ? token.colorError : token.colorSuccess, fontSize: 22 }}
          />
          <Progress
            percent={stats.percent}
            showInfo={false}
            size="small"
            strokeColor={bad ? token.colorError : token.colorSuccess}
            style={{ margin: 0 }}
          />
          {/* ⚠️ Namuna hajmi OCHIQ aytiladi — sabab klass javadoc'ida */}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('dashboard.authHealth.sample', { rejected: stats.rejected, total: stats.total })}
          </Typography.Text>
        </Space>
      )}
    </Card>
  )
}
